/**
 * API de depuracao exposta em window.__GAME__.
 *
 * Existe para que a revisao visual automatizada (Playwright) consiga posicionar
 * a camera em pontos conhecidos, esconder a interface, trocar a qualidade e
 * esperar a imagem estabilizar antes de tirar o print - sem precisar "jogar".
 *
 * Nao e usada pelo jogo em si. Nao remova: o pipeline de critica depende dela.
 */

export function installDebugApi(ctx) {
  const { engine, settings } = ctx;

  const api = {
    ctx,
    version: 1,
    ready: true,

    /** Comeca a partida pulando o menu (sem exigir pointer lock). */
    start() {
      ctx.game?.start?.();
      ctx.events.emit(ctx.EVENTS.GAME_STATE, { state: 'playing' });
      engine.paused = false;
      return true;
    },

    pause(on = true) {
      engine.paused = !!on;
      return engine.paused;
    },

    /** Posiciona a camera do jogador. Angulos em graus. */
    pose(x, y, z, yawDeg = 0, pitchDeg = 0) {
      const cam = ctx.camera;
      cam.position.set(x, y, z);
      cam.rotation.set((pitchDeg * Math.PI) / 180, (yawDeg * Math.PI) / 180, 0, 'YXZ');
      if (ctx.cameraRig?.setPose) ctx.cameraRig.setPose(cam.position, (yawDeg * Math.PI) / 180, (pitchDeg * Math.PI) / 180);
      if (ctx.controller?.setPosition) ctx.controller.setPosition(x, y - 1.65, z);
      return true;
    },

    /** Congela o mundo mas mantem a camera livre para inspecao visual. */
    freecam(on = true) {
      this._freecam = !!on;
      if (ctx.controller) ctx.controller.enabled = !on;
      return this._freecam;
    },

    /** Esconde toda a interface (para julgar so a imagem renderizada). */
    hud(on = true) {
      const root = document.getElementById('ui-root');
      if (root) root.style.display = on ? '' : 'none';
      return on;
    },

    quality(name) {
      settings.setQuality(name);
      return settings.quality;
    },

    set(key, value) {
      settings.set(key, value);
      return settings[key];
    },

    spawnBots(n = 4) {
      return ctx.bots?.spawn?.(n) ?? 0;
    },

    killAllBots() {
      return ctx.bots?.killAll?.() ?? 0;
    },

    /** Espera N frames desenhados. Necessario antes de print: TAA/bloom/exposicao levam alguns frames. */
    settle(frames = 12) {
      return new Promise((resolve) => {
        let left = Math.max(1, frames);
        const step = () => {
          if (--left <= 0) resolve(true);
          else requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    },

    stats() {
      const info = ctx.renderer?.info;
      return {
        fps: Math.round(engine.fps),
        frame: engine.frame,
        drawCalls: info?.render?.calls ?? -1,
        triangles: info?.render?.triangles ?? -1,
        programs: info?.programs?.length ?? -1,
        textures: info?.memory?.textures ?? -1,
        geometries: info?.memory?.geometries ?? -1,
        quality: settings.quality,
        bots: ctx.bots?.count?.() ?? 0,
        health: ctx.player?.health ?? -1,
        state: ctx.game?.state ?? '?'
      };
    },

    /** Pontos de vista uteis para comparacao visual. Preenchido pelo Level. */
    viewpoints() {
      return ctx.level?.viewpoints ?? [];
    },

    /** Vai para um ponto de vista nomeado ou por indice. */
    async goto(which, settleFrames = 14) {
      const list = this.viewpoints();
      const vp = typeof which === 'number' ? list[which] : list.find((v) => v.name === which);
      if (!vp) return false;
      this.pose(vp.position[0], vp.position[1], vp.position[2], vp.yaw ?? 0, vp.pitch ?? 0);
      await this.settle(settleFrames);
      return true;
    },

    errors: []
  };

  window.addEventListener('error', (e) => api.errors.push(String(e.message)));
  window.addEventListener('unhandledrejection', (e) => api.errors.push(String(e.reason)));

  window.__GAME__ = api;
  return api;
}
